/**
 * horarioBienestarUtils.js
 *
 * Utilidades del horario de recepción de solicitudes. El backend es la fuente
 * de verdad (decide si está abierto y redacta el mensaje); aquí solo se
 * normaliza lo que llega y se le da forma para pintarlo.
 */

export const DIAS_SEMANA = Object.freeze([
    { valor: 0, corto: 'Lun', largo: 'Lunes' },
    { valor: 1, corto: 'Mar', largo: 'Martes' },
    { valor: 2, corto: 'Mié', largo: 'Miércoles' },
    { valor: 3, corto: 'Jue', largo: 'Jueves' },
    { valor: 4, corto: 'Vie', largo: 'Viernes' },
    { valor: 5, corto: 'Sáb', largo: 'Sábado' },
    { valor: 6, corto: 'Dom', largo: 'Domingo' },
]);

export const HORA_INICIO_SUGERIDA = '08:00';
export const HORA_FIN_SUGERIDA = '17:00';

/** '08:00:00' | '08:00' -> '08:00' (lo que espera un input type="time"). */
export const aHoraInput = (valor) => {
    const texto = String(valor || '').trim();
    if (!texto) return '';
    const partes = texto.split(':');
    if (partes.length < 2) return '';
    return `${partes[0].padStart(2, '0')}:${partes[1].padStart(2, '0')}`;
};

/** '08:00' -> '8:00 a. m.' — mismo formato que usa el backend en los mensajes. */
export const aHoraLegible = (valor) => {
    const hhmm = aHoraInput(valor);
    if (!hhmm) return '';
    const [h, m] = hhmm.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return '';
    const sufijo = h < 12 ? 'a. m.' : 'p. m.';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, '0')} ${sufijo}`;
};

/** Versión compacta para columnas estrechas: '8:00 a.m.' en vez de '8:00 a. m.'. */
export const aHoraCompacta = (valor) => aHoraLegible(valor).replace('a. m.', 'a.m.').replace('p. m.', 'p.m.');

/** ¿Hay al menos un día con franja definida? */
export const tieneHorarioConfigurado = (dias) => (
    (Array.isArray(dias) ? dias : []).some((d) => d && d.abierto && d.hora_inicio && d.hora_fin)
);

/** Franja de un día lista para mostrar, o 'Cerrado'. */
export const franjaLegible = (dia) => {
    if (!dia || !dia.abierto || !dia.hora_inicio || !dia.hora_fin) return 'Cerrado';
    return `${aHoraLegible(dia.hora_inicio)} – ${aHoraLegible(dia.hora_fin)}`;
};

/**
 * Los siete días en orden, completando con "cerrado" los que el backend no
 * traiga todavía. Así el formulario siempre pinta la semana entera.
 */
export const normalizarDias = (dias) => {
    const porDia = new Map(
        (Array.isArray(dias) ? dias : []).map((d) => [Number(d.dia_semana), d]),
    );
    return DIAS_SEMANA.map(({ valor }) => {
        const dia = porDia.get(valor) || {};
        const abierto = Boolean(dia.abierto);
        return {
            dia_semana: valor,
            abierto,
            hora_inicio: aHoraInput(dia.hora_inicio) || (abierto ? HORA_INICIO_SUGERIDA : ''),
            hora_fin: aHoraInput(dia.hora_fin) || (abierto ? HORA_FIN_SUGERIDA : ''),
        };
    });
};

/**
 * Valida la semana antes de enviarla. Devuelve { [dia_semana]: mensaje }
 * vacío cuando todo está bien. Duplica a propósito la validación del
 * serializer: aquí es para no perder el viaje, allá es la que manda.
 */
export const validarDias = (dias, restriccionActiva) => {
    const errores = {};
    (Array.isArray(dias) ? dias : []).forEach((dia) => {
        if (!dia.abierto) return;
        if (!dia.hora_inicio || !dia.hora_fin) {
            errores[dia.dia_semana] = 'Indica la hora de inicio y la de cierre.';
            return;
        }
        if (dia.hora_fin <= dia.hora_inicio) {
            errores[dia.dia_semana] = 'La hora de cierre debe ser posterior a la de inicio.';
        }
    });

    const algunoAbierto = (dias || []).some((d) => d.abierto);
    if (restriccionActiva && !algunoAbierto) {
        errores.general = 'Deja al menos un día abierto para activar la restricción por horario.';
    }
    return errores;
};

/** Payload que espera el endpoint: los siete días siempre. */
export const construirPayloadHorario = (dias, restriccionActiva, lineaAtencion) => ({
    restriccion_activa: Boolean(restriccionActiva),
    linea_atencion: String(lineaAtencion || '').trim(),
    dias: normalizarDias(dias).map((dia) => ({
        dia_semana: dia.dia_semana,
        abierto: dia.abierto,
        hora_inicio: dia.abierto ? dia.hora_inicio : null,
        hora_fin: dia.abierto ? dia.hora_fin : null,
    })),
});

/**
 * ¿Se debe bloquear la creación de solicitudes?
 * Ante cualquier duda (respuesta nula, forma inesperada) responde que NO:
 * un fallo de configuración no puede dejar a un estudiante sin pedir atención.
 */
export const debeBloquearCreacion = (horario) => {
    if (!horario || typeof horario !== 'object') return false;
    if (!horario.restriccion_activa) return false;
    return horario.abierto_ahora === false;
};
